import _ from 'lodash';

export interface ICommentReasonProblem {
    /** Field / role label, e.g. "Rendering provider" or "Facility". */
    slot: string;
    /** Optional person/org name when available. */
    who?: string;
    /** Invalid value to show struck-through (e.g. NPI). */
    value?: string;
    /** Short explanation of why it failed. */
    why: string;
}

export interface IParsedCommentReason {
    /** Human-readable error summary (outside fenced content). */
    headline: string;
    /** Card title override when we can classify the error. */
    title?: string;
    /** Label for the explain line (e.g. "Coder rule" / "Error"). */
    explainLabel?: string;
    /** Body text under the explain label. */
    explain?: string;
    /** Footer "Missing" cell value when known. */
    missingLabel?: string;
    /** Structured fix-list rows for the Nana-style problems table. */
    problems?: ICommentReasonProblem[];
    /** Raw fenced content (engine output or claim payload). */
    payloadRaw?: string;
    /** Parsed JSON from a Payload fence, when valid. */
    payloadJson?: unknown;
    /** Collapsible raw section label. */
    rawLabel?: string;
    /** Kind of structured body we matched. */
    kind: 'payload' | 'coding_engine';
}

const PAYLOAD_BLOCK_RE =
    /(?:^|\n)\s*Payload:\s*(?:\r?\n)+\s*```(?:json|javascript|js)?[ \t]*\r?\n?([\s\S]*?)```/i;

const CODING_ENGINE_FENCE_RE =
    /(?:The coding engine has returned the following error:\s*)?```([\s\S]*?)```/i;

/** Path line that starts a violation block: "<path> is invalid." */
const INVALID_PATH_LINE_RE = /^(.+?)\s+is\s+invalid\.\s*$/i;

const INDEXED_PATH_RE = /^([A-Za-z_][\w.]*)\s*\[\s*(\d+)\s*\]$/;

const CODING_ENGINE_EXPLAIN =
    'ENTER’s coding engine flagged one or more fields on this claim before submission. Correct them in your EMR, or fix the claim in ENTER.';

/**
 * Chronologically first comment (earliest `created`), not array index 0.
 * Comments with missing/invalid `created` sort last.
 */
export function getEarliestComment<T extends { created?: string | null }>(
    comments: T[] | undefined | null,
): T | undefined {
    if (!comments?.length) {
        return undefined;
    }
    return _.minBy(comments, c => {
        const t = c.created ? Date.parse(String(c.created)) : NaN;
        return Number.isFinite(t) ? t : Number.POSITIVE_INFINITY;
    });
}

function titleCasePath(path: string): string {
    return path
        .replace(/[_.]+/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/\b\w/g, ch => ch.toUpperCase());
}

/** Indexed paths like `providers [0]` → `Provider 1` (order is not role-stable). */
function labelForIndexedEntity(entity: string, index: number): string {
    const singular = entity.replace(/s$/i, '');
    return `${titleCasePath(singular)} ${index + 1}`;
}

function pathToSlot(path: string): string {
    const trimmed = path.trim();
    const indexed = trimmed.match(INDEXED_PATH_RE);
    if (indexed) {
        return labelForIndexedEntity(indexed[1], Number.parseInt(indexed[2], 10));
    }
    return titleCasePath(trimmed);
}

function splitMissingFields(line: string): string[] | null {
    const match = line.match(/^Missing fields:\s*(.+)$/i);
    if (!match) {
        return null;
    }
    return match[1]
        .split(',')
        .map(part => part.trim())
        .filter(Boolean);
}

function parseInvalidFormatLine(
    line: string,
): { field?: string; value?: string; why: string } | null {
    // Keep the lead-in phrase from the engine text as `why` (readable, no invented copy).
    const match = line.match(
        /^(Invalid format for field):\s*([^\n—\-]+?)(?:\s*[—\-]\s*Invalid\s+[^:]+:\s*(.+))?$/i,
    );
    if (!match) {
        return null;
    }

    return {
        why: match[1],
        field: match[2].trim(),
        value: match[3]?.trim(),
    };
}

/**
 * Generic parser for coding-engine fenced dumps shaped as:
 *   <path> is invalid.
 *     <reason line(s)>
 *
 * Handles providers[N], attributes, services[N], Missing fields lists, etc.
 */
export function parseInvalidRowsBlock(raw: string): ICommentReasonProblem[] {
    const trimmed = (raw || '').trim();
    if (!trimmed) {
        return [];
    }

    // Some payloads keep literal "\n" escapes inside the fence; normalize before splitting.
    const normalized = trimmed.replace(/\\n/g, '\n');
    const lines = normalized.split(/\r?\n/);
    const blocks: { path: string; reasons: string[] }[] = [];
    let current: { path: string; reasons: string[] } | null = null;

    for (const line of lines) {
        const pathMatch = line.trim().match(INVALID_PATH_LINE_RE);
        if (pathMatch) {
            if (current) {
                blocks.push(current);
            }
            current = { path: pathMatch[1].trim(), reasons: [] };
            continue;
        }
        if (current) {
            const reason = line.trim();
            if (reason) {
                current.reasons.push(reason);
            }
        }
    }
    if (current) {
        blocks.push(current);
    }

    const problems: ICommentReasonProblem[] = [];

    for (const block of blocks) {
        const pathSlot = pathToSlot(block.path);

        if (!block.reasons.length) {
            problems.push({
                slot: pathSlot,
                why: 'is invalid',
            });
            continue;
        }

        let emittedFromReasons = false;

        for (const reason of block.reasons) {
            const missingFields = splitMissingFields(reason);
            if (missingFields) {
                for (const field of missingFields) {
                    problems.push({
                        // Keep the parent path (patient / attributes / …) as the slot;
                        // put the concrete missing field in `who` so nothing is dropped.
                        slot: pathSlot,
                        who: titleCasePath(field),
                        why: 'Missing fields',
                    });
                }
                emittedFromReasons = true;
                continue;
            }

            const format = parseInvalidFormatLine(reason);
            if (format) {
                problems.push({
                    slot: pathSlot,
                    who: format.field ? titleCasePath(format.field) : undefined,
                    value: format.value,
                    why: format.why,
                });
                emittedFromReasons = true;
                continue;
            }

            const npiOnly = reason.match(/^(Invalid\s+NPI):\s*([0-9]+)/i);
            if (npiOnly) {
                problems.push({
                    slot: pathSlot,
                    value: npiOnly[2],
                    why: npiOnly[1],
                });
                emittedFromReasons = true;
                continue;
            }

            problems.push({
                slot: pathSlot,
                why: reason,
            });
            emittedFromReasons = true;
        }

        if (!emittedFromReasons) {
            problems.push({
                slot: pathSlot,
                why: 'is invalid',
            });
        }
    }

    // Fallback: fenced blob mentions an NPI but has no "<path> is invalid." headers.
    if (!problems.length) {
        const npiMatch = trimmed.match(/(Invalid\s+NPI):\s*([0-9]+)/i);
        if (npiMatch) {
            problems.push({
                slot: 'Provider NPI',
                value: npiMatch[2],
                why: npiMatch[1],
            });
        }
    }

    return problems;
}

/** @deprecated Use parseInvalidRowsBlock — kept as alias for any external imports. */
export const parseCodingEngineProblems = parseInvalidRowsBlock;

function parsePayloadReason(body: string): IParsedCommentReason | null {
    const match = body.match(PAYLOAD_BLOCK_RE);
    if (!match || match.index === undefined) {
        return null;
    }

    const payloadRaw = (match[1] || '').trim();
    let payloadJson: unknown;
    if (payloadRaw) {
        try {
            payloadJson = JSON.parse(payloadRaw);
        } catch {
            // Keep raw only when JSON is invalid.
        }
    }

    const beforePayload = body.slice(0, match.index).trim();
    const headline = beforePayload.replace(/^Validation errors:\s*/i, '').trim();

    if (!headline && !payloadRaw) {
        return null;
    }

    return {
        kind: 'payload',
        headline: headline || 'Validation error',
        title: 'Validation errors',
        payloadRaw: payloadRaw || undefined,
        payloadJson,
    };
}

function parseCodingEngineReason(body: string): IParsedCommentReason | null {
    const hasCodingEnginePhrase = /coding engine/i.test(body);
    const hasInvalidFence = /```[\s\S]*?\bis\s+invalid\./i.test(body);

    if (!hasCodingEnginePhrase && !hasInvalidFence) {
        return null;
    }

    const match = body.match(CODING_ENGINE_FENCE_RE);
    if (!match || match.index === undefined) {
        return null;
    }

    const payloadRaw = (match[1] || '').trim();
    if (!payloadRaw) {
        return null;
    }

    const problems = parseInvalidRowsBlock(payloadRaw);
    const after = body.slice(match.index + match[0].length).replace(/^\s*\.?\s*/, '').trim();
    const hasProblems = problems.length > 0;

    const headline =
        after ||
        (hasProblems
            ? `${problems.length} issue${problems.length === 1 ? '' : 's'} need${problems.length === 1 ? 's' : ''} fixing.`
            : 'The coding engine returned an error.');

    if (!hasProblems) {
        return {
            kind: 'coding_engine',
            headline,
            title: 'Validation errors',
            payloadRaw,
        };
    }

    return {
        kind: 'coding_engine',
        headline,
        title: 'Coding problem',
        explainLabel: 'Coder rule',
        explain: CODING_ENGINE_EXPLAIN,
        missingLabel: _.uniq(problems.map(p => (p.who ? `${p.slot} · ${p.who}` : p.slot))).join(', '),
        problems,
        payloadRaw,
        rawLabel: 'Coding engine output',
    };
}

/**
 * Parse structured opening-comment bodies:
 *  - "Validation errors: … Payload: ```…```"
 *  - "The coding engine has returned the following error: ```… is invalid.…```. Please…"
 * Returns null when the body is not in a recognized shape.
 */
export function parseCommentReason(body?: string | null): IParsedCommentReason | null {
    if (!body || !body.trim()) {
        return null;
    }

    return parsePayloadReason(body) || parseCodingEngineReason(body);
}

/** Body text to show in the chat header when a structured reason was parsed (no giant fence). */
export function formatCommentReasonDisplay(parsed: IParsedCommentReason): string {
    if (parsed.kind === 'coding_engine') {
        return parsed.headline;
    }
    return `Validation errors:\n\n${parsed.headline}`;
}
